import { useCallback, useContext, useEffect } from 'react';
import styled from '@emotion/styled';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { StopCircle } from 'react-feather';
import {
  InitiateQuerySpeechToText,
  TextToSpeechStateContext,
  StopTextToSpeechContext,
  VoiceModeContext,
} from '@trinity/chat-components/containers/TextToSpeechAudioPlayer/Context';
import { ChatResponseType } from '@trinity/chat';
import { BaseMessageButton } from './Message.Button';
import { ActivityIndicator } from '../presentation/ActivityIndicator';
import { useHasChatReadAloudEnabled } from '@trinity/chat-components/containers/Chat/selectors';

type Props = {
  kolSearchId?: number | null;
  queryId?: number | null;
};

export const ReadAloudButton = ({ kolSearchId, queryId }: Props) => {
  const hasChatReadAloudEnabled = useHasChatReadAloudEnabled();

  const initiateReadAloud = useContext(InitiateQuerySpeechToText);
  const stopTextToSpeech = useContext(StopTextToSpeechContext);
  const audioState = useContext(TextToSpeechStateContext);
  const voicemode = useContext(VoiceModeContext);

  const isActive = audioState && (audioState.kolSearchId === kolSearchId || audioState.queryId === queryId);
  const isPlaying = audioState?.state === 'playing' && isActive;
  const isLoading = audioState?.state === 'loading' && isActive;
  const isError = audioState?.state === 'error' && isActive;

  const startPlaying = useCallback(async () => {
    if (isPlaying) {
      stopTextToSpeech?.();
      return;
    }

    return initiateReadAloud({
      kolSearchId,
      queryId,
    });
  }, [isPlaying, stopTextToSpeech, initiateReadAloud, kolSearchId, queryId]);

  const autoPlayReadAloud = useCallback(() => {
    if (voicemode.enabled && voicemode.type !== ChatResponseType.HcpSummary && voicemode.queryId === queryId) {
      if (audioState.state === 'playing') {
        stopTextToSpeech?.();
      }
      startPlaying();
      voicemode.dequeue();
    }
  }, [
    audioState,
    startPlaying,
    queryId,
    stopTextToSpeech,
    voicemode,
  ]);

  useEffect(() => {
    autoPlayReadAloud();
  }, []);

  if (!hasChatReadAloudEnabled) return null;

  if (isPlaying) {
    return (
      <StyledButton onClick={() => stopTextToSpeech()}>
        <FilledStopCircle size={18} />
        <TextLabel>Stop</TextLabel>
      </StyledButton>
    );
  }

  if (isLoading) {
    return (
      <StyledButton>
        <ActivityIndicator />
      </StyledButton>
    );
  }

  if (isError) {
    return (
      <StyledButton>
        <TextLabel>Error</TextLabel>
      </StyledButton>
    );
  }

  return (
    <StyledButton onClick={startPlaying}>
      <PlayArrowIcon fontSize="small" />
      <TextLabel>Read Aloud</TextLabel>
    </StyledButton>
  );
};

const StyledButton = styled(BaseMessageButton)({
  display: 'flex',
  justifyContent: 'left',
  gap: '4px',
  width: 115,
  '@container message-actions (max-width: 450px)': {
    minWidth: 'auto',
    width: 'auto',
  },
});

const TextLabel = styled.span({
  '@container message-actions (max-width: 450px)': {
    display: 'inline-block',
    maxWidth: 0,
    overflow: 'hidden',
    opacity: 0,
    whiteSpace: 'nowrap',
    transition: 'max-width 0.2s ease, opacity 0.2s ease',
    'button:hover &': {
      maxWidth: 100,
      opacity: 1,
    },
  },
});

const FilledStopCircle = styled(StopCircle)`
  circle {
    fill: none;
  }
  rect {
    fill: currentColor;
  }
`;
